// useTeacherHomeData.js
// Data hook backing the teacher home screen (Option A redesign) — calls the
// aggregate B1 endpoint (GET /home/teacher-summary), replacing the old N+1
// per-test fetches. See
// docs/superpowers/specs/2026-09-16-home-redesign.md §11 "WP3 (revised)".
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { FASTAPI_BASE_URL } from "../../../config";
import { mapSummary } from "./teacherHomeMap";

const identity = (s) => s;

const EMPTY_STATE = {
  students: [],
  attention: [],
  needsReteaching: [],
  weekStats: { active: 0, total: 0, avgMastery: null, questions: 0, quizzes: 0 },
  toReview: [],
  window: null,
};

/**
 * @param {object} params
 * @param {string} params.userEmail - identifies the signed-in teacher; kept
 *   in the signature for callers/readability but intentionally NOT a fetch
 *   dependency (B1 authorizes via the Bearer token alone, and this value is
 *   never sent as a request param).
 * @param {string} params.authToken
 * @param {number|string|null|undefined} params.classId - null/undefined skips
 *   the request entirely and every slice stays empty.
 * @param {number} [params.weeks]
 * @param {object} [params.handlers] - { open_test, open_mastery, open_topic,
 *   message_student, grade_test } -> (payload) => void. An attention row
 *   whose action type has no matching handler gets `action: null`. A new
 *   object identity on every render must NOT retrigger the fetch - only the
 *   memoized mapping below depends on it.
 * @param {(s: string) => string} [params.t]
 */
export default function useTeacherHomeData({
  userEmail,
  authToken,
  classId,
  weeks = 4,
  handlers = {},
  t = identity,
}) {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (classId == null) {
      setRaw(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

    axios
      .get(`${FASTAPI_BASE_URL}/home/teacher-summary`, {
        params: { class_id: classId, weeks },
        headers,
      })
      .then((res) => {
        if (cancelled) return;
        setRaw(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("useTeacherHomeData: failed to fetch teacher summary", err);
        setRaw(null);
        setError("Failed to load teacher summary.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userEmail is
    // intentionally excluded (see JSDoc); handlers/t are excluded because
    // they are new-identity-every-render callbacks that must only affect the
    // memoized mapping below, not the fetch itself.
  }, [authToken, classId, weeks]);

  const data = useMemo(() => (raw ? mapSummary(raw, handlers, t) : EMPTY_STATE), [raw, handlers, t]);

  return { ...data, loading, error };
}
